import React, { useEffect, useState } from "react";
import { api } from "../../../lib/api";

export function InstallFunnelTile() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/admin/install-funnel")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, []);

  if (!data) return null;
  if (!data.total_views) return null;

  const conv = ((data.conversion_rate || 0) * 100).toFixed(1);
  const platforms = Object.entries(data.by_platform || {});
  const sources = Object.entries(data.by_source || {});
  const series = data.daily_series || [];
  const wow = data.wow || {};

  return (
    <div
      className={`mt-4 rounded-xl border bg-white p-4 ${wow.alert ? "border-rose-300 ring-1 ring-rose-200" : "border-[#E5E0D8]"}`}
      data-testid="install-funnel-tile"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-[#112318]">Install funnel</p>
            {wow.alert && (
              <span
                className="text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200"
                data-testid="install-funnel-alert"
                title={`Week-over-week conversion dropped ${wow.delta_pct}%`}
              >
                ↓ {Math.abs(wow.delta_pct || 0)}% wow
              </span>
            )}
          </div>
          <p className="text-xs text-[#4A5C50] mt-1">
            Last {data.window_days} days · {data.total_views} view{data.total_views === 1 ? "" : "s"} →
            {" "}{data.total_completions} completion{data.total_completions === 1 ? "" : "s"} ·{" "}
            <span className="mono" data-testid="install-funnel-conversion">{conv}%</span>
          </p>
        </div>
        {series.length > 0 && (
          <FunnelSparkline series={series} alert={wow.alert} />
        )}
      </div>

      {wow.alert && (
        <div
          className="mt-3 rounded-lg p-2.5 text-[11px] bg-rose-50 border border-rose-200 text-rose-900"
          data-testid="install-funnel-alert-detail"
        >
          Conversion dropped from <span className="mono">{((wow.prev_week_conversion || 0) * 100).toFixed(1)}%</span>
          {" "}last week to <span className="mono">{((wow.this_week_conversion || 0) * 100).toFixed(1)}%</span>
          {" "}this week. Check whether the QR code, /install page, or PWA manifest is broken.
        </div>
      )}

      {platforms.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {platforms.map(([p, v]) => (
            <div
              key={p}
              className="rounded-lg border border-[#E5E0D8] p-2.5 bg-[#FBF9F6]"
              data-testid={`install-funnel-platform-${p}`}
            >
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#7A8B82]">{p}</p>
              <p className="mono text-sm text-[#0A1F16] mt-1">{v.views}→{v.completions}</p>
              <p className="text-[10px] text-[#4A5C50] mono">{((v.conversion_rate || 0) * 100).toFixed(1)}%</p>
            </div>
          ))}
        </div>
      )}

      {sources.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]" data-testid="install-funnel-sources">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#7A8B82]">Sources</span>
          {sources.map(([s, n]) => (
            <span key={s} className="px-2 py-0.5 rounded-full bg-[#184A30]/10 text-[#184A30] mono">
              {s}: {n}
            </span>
          ))}
        </div>
      )}

      <p className="mt-2 text-[10px] text-[#7A8B82] italic">{data.note}</p>
    </div>
  );
}

function FunnelSparkline({ series, alert }) {
  const W = 96;
  const H = 28;
  const pad = 2;
  const max = Math.max(1, ...series.map((d) => d.views));
  const step = (W - pad * 2) / Math.max(1, series.length - 1);
  const points = series.map((d, i) => {
    const x = pad + i * step;
    const y = pad + (H - pad * 2) * (1 - d.views / max);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const stroke = alert ? "#E11D48" : "#184A30";
  return (
    <div
      className="flex flex-col items-end shrink-0"
      data-testid="install-funnel-sparkline"
      title={`7-day daily views: ${series.map((d) => d.views).join(", ")}`}
    >
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        <polyline
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
        {series.length > 0 && (() => {
          const last = series[series.length - 1];
          const x = pad + (series.length - 1) * step;
          const y = pad + (H - pad * 2) * (1 - last.views / max);
          return <circle cx={x} cy={y} r="2" fill={stroke} />;
        })()}
      </svg>
      <p className="text-[9px] tracking-[0.2em] uppercase text-[#7A8B82] mt-0.5">7-day views</p>
    </div>
  );
}
